import React, { useEffect, useRef, useState } from "react";
import Header from "./header";
import UTILS from '../../utils/commons';
import Loading from "./Loading";
import feature from "../../img/customer.svg";
import { X, Pen } from 'lucide-react';
import { useNavigate } from "react-router";
import { ApiClient } from "../../utils/api-client";
import { AxiosError } from "axios";

const Settings = props => {
    const navigate = useNavigate();
    const loginState = JSON.parse( window.sessionStorage.getItem( "loginState" ) );
    const apiClient = new ApiClient( loginState?.accessToken );
    const fileInputRef = useRef( null );

    const [ feedback, setFeedback ] = useState( "" );
    const [ isLoading, setIsLoading ] = useState( false );
    const [ showEditForm, setShowEditForm ] = useState( false );
    const [ pageData, setPageData ] = useState( loginState );
    const [ previewImage, setPreviewImage ] = useState(
        loginState?.profile?.profileImage || feature,
    );

    const updateSessionState = ( updates ) => {
        const merged = { ...pageData, ...updates };
        setPageData( merged );
        window.sessionStorage.setItem( 'loginState', JSON.stringify( merged ) );
    };

    const handleSubmit = async ( e ) => {
        e.preventDefault();
        setIsLoading( true );
        setFeedback( '' );
        try {
            const payload = {
                firstName: e.target.firstName.value.trim(),
                lastName: e.target.lastName.value.trim(),
            };

            if ( pageData?.role === UTILS.SALES ) {
                payload.serviceArea = e.target.serviceArea.value.trim();
            }
            if ( e.target.password.value ) {
                payload.password = e.target.password.value;
            }

            let response;
            if ( pageData?.role === UTILS.SALES && pageData?.profile?.id ) {
                response = await apiClient.patch( `/profiles/${pageData.profile.id}`, payload );
            } else {
                response = await apiClient.patch( `/auth/${pageData.id}`, payload );
            }

            if ( response?.status === 200 ) {
                updateSessionState( {
                    firstName: payload.firstName,
                    lastName: payload.lastName,
                    profile: {
                        ...pageData?.profile,
                        serviceArea: payload.serviceArea ?? pageData?.profile?.serviceArea,
                    },
                } );
                setShowEditForm( false );
                setFeedback( 'Profile updated successfully.' );
            } else {
                setFeedback( 'Failed to update profile.' );
            }
        } catch ( err ) {
            const msg = err instanceof AxiosError
                ? err.response?.data?.message || err.response?.data?.error || 'Failed to update profile.'
                : 'Failed to update profile.';
            setFeedback( String( msg ) );
        } finally {
            setIsLoading( false );
        }
    };

    const handleCameraClick = () => {
        if ( fileInputRef.current ) {
            fileInputRef.current.click();
        }
    };

    const uploadImage = async ( base64Image ) => {
        if ( !pageData?.profile?.id ) {
            setFeedback( 'Profile image is only available for profile accounts.' );
            return;
        }
        setIsLoading( true );
        try {
            const req = await apiClient.patch( `/profiles/${pageData.profile.id}`, { profileImage: base64Image } );
            if ( req?.status === 200 ) {
                updateSessionState( {
                    profile: {
                        ...pageData.profile,
                        profileImage: base64Image,
                    },
                } );
                setFeedback( 'Profile image updated successfully.' );
            }
        } catch ( err ) {
            const msg = err instanceof AxiosError
                ? err.response?.data?.message || err.response?.data?.error || 'Failed to update profile image.'
                : 'Failed to update profile image.';
            setFeedback( String( msg ) );
        } finally {
            setIsLoading( false );
        }
    };

    const handleImageInputChange = ( e ) => {
        const file = e.target.files?.[ 0 ];
        if ( !file ) return;

        const localUrl = URL.createObjectURL( file );
        setPreviewImage( localUrl );

        const reader = new FileReader();
        reader.onload = () => {
            uploadImage( reader.result );
        };
        reader.readAsDataURL( file );
    };

    useEffect( () => {
        if ( !loginState ) navigate( "/signin", { replace: true, state: { target: '/settings' } } );
    }, [] );

    return ( <>
        <Header auth={pageData} />
        <section className="container mx-auto mt-8">
            {feedback ? <div className="w-10/12 md:w-8/12 mx-auto mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-blue-700 text-sm">{feedback}</div> : null}
            <div className="rounded-xl border border-gray-200 bg-white shadow-md p-4 md:p-6">
                <div className="w-full mb-5 flex justify-between items-start">
                    <div>
                        <p className="text-sm uppercase tracking-wide text-gray-500">Account</p>
                        <p className="text-2xl font-semibold text-gray-800">Profile Settings</p>
                    </div>
                    <div className="flex items-start justify-start gap-4">
                        <button className="p-1 rounded-md hover:bg-gray-100 transition-colors" onClick={() => { navigate( -1, { replace: true } ) }}>
                            <X className="text-primary-500 hover:text-secondary-500" />
                        </button>
                        <button className="p-1 rounded-md hover:bg-gray-100 transition-colors" onClick={() => { setShowEditForm( true ) }}>
                            <Pen className="text-primary-500 hover:text-secondary-500" />
                        </button>
                    </div>
                </div>

                <div className="w-full rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 p-2 md:p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
                        <div className="flex flex-col lg:flex-col items-center lg:items-start gap-4">
                            <img
                                id="preview"
                                alt={pageData?.firstName}
                                src={previewImage}
                                className="h-28 w-28 rounded-full object-cover border-2 border-primary-200 shadow-sm mx-auto cursor-pointer"
                                onClick={handleCameraClick}
                            />
                            <input ref={fileInputRef} type="file" id="avatar" className="hidden" accept="image/*" onChange={handleImageInputChange} />
                            <div className="text-center sm:text-left lg:text-left">
                                <p className="text-xl font-semibold text-gray-800">{`${pageData?.firstName || ''} ${pageData?.lastName || ''}`.trim()}</p>
                                <p className="text-sm text-gray-500 mt-1">{pageData?.email || 'No email'}</p>
                                <p className="inline-block mt-3 text-xs font-semibold uppercase tracking-wide text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full">{pageData?.role || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">First Name</p>
                                <p className="text-secondary-700 font-medium mt-1">{pageData?.firstName || 'N/A'}</p>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Last Name</p>
                                <p className="text-secondary-700 font-medium mt-1">{pageData?.lastName || 'N/A'}</p>
                            </div>
                            {pageData?.role === UTILS.SALES ? <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Service Area</p>
                                <p className="text-secondary-700 font-medium mt-1">{pageData?.profile?.serviceArea || 'N/A'}</p>
                            </div> : null}
                            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Phone</p>
                                <p className="text-secondary-700 font-medium mt-1">{pageData?.profile?.phone || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {showEditForm ? <div className="w-full flex justify-start items-center mt-4">
                    <form className="w-full bg-white p-4 rounded-lg border border-gray-200 shadow-sm grid grid-cols-1 md:grid-cols-4 mx-auto gap-4 p-2 md:p-6" onSubmit={handleSubmit}>
                        <div className="w-full">
                            <label htmlFor="firstName" className="block text-sm font-medium">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                className="form-control"
                                defaultValue={pageData?.firstName || ''}
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium">Last Name</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                className="form-control"
                                defaultValue={pageData?.lastName || ''}
                                required
                            />
                        </div>
                        {pageData?.role === UTILS.SALES ? <div>
                            <label htmlFor="serviceArea" className="block text-sm font-medium">Service Area</label>
                            <input
                                type="text"
                                id="serviceArea"
                                name="serviceArea"
                                className="form-control"
                                defaultValue={pageData?.profile?.serviceArea || ''}
                                required
                            />
                        </div> : null}

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium">Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                className="form-control"
                                placeholder="Leave blank to keep current password"
                            />
                        </div>
                        <div className="md:flex justify-between gap-4 mt-2 space-y-4">
                            <button type="submit" className="w-full bg-primary-500 text-white px-2 py-2 rounded">{isLoading ? 'Updating...' : 'Update'}</button>
                            <button type="button" className="w-full bg-gray-300 px-2 py-2 rounded" onClick={() => setShowEditForm( false )}>Cancel</button>
                        </div>
                    </form>
                </div> : null}
            </div>
            {isLoading ? <Loading className="mx-auto mt-6" /> : null}
        </section>
    </> );

}
export default Settings
